import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AuthAudienceId, AuthPlantationCrop } from '../../../content/authFeatureShowcase';
import logo from '../../../assets/digisaka-official-logo.png';
import authBlo from '../../../assets/auth/blo.jpg';
import authPlantation from '../../../assets/auth/plantation.jpg';
import authLgu from '../../../assets/auth/lgu.jpg';
import authProjectDeveloper from '../../../assets/auth/project-developer.jpg';
import AuthFeatureShowcase, { FieldBoundaryLines } from './AuthFeatureShowcase';
import {
  AUTH_FONT_SANS,
  AUTH_GREEN,
  AUTH_GREEN_BORDER,
  AUTH_GREEN_LIGHT,
  AUTH_GREEN_SOFT,
  AUTH_INK,
  AUTH_LABEL,
  AUTH_LINE,
  AUTH_MUTED,
  AUTH_PAPER,
  AUTH_PLACEHOLDER,
  AUTH_SURFACE,
} from './authTheme';

const AUTH_PANEL_IMAGES: Record<AuthAudienceId, string> = {
  blo: authBlo,
  plantation: authPlantation,
  lgu: authLgu,
  project_developer: authProjectDeveloper,
};

interface AuthPageShellProps {
  title: string;
  subtitle: string;
  badge?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Sync left-panel feature tabs with signup role. */
  activeAudience?: AuthAudienceId;
  plantationCrop?: AuthPlantationCrop;
  onAudienceChange?: (id: AuthAudienceId) => void;
  onPlantationCropChange?: (crop: AuthPlantationCrop) => void;
  /** When set, the left panel cycles BLO, Plantations, LGU, and Carbon. */
  autoAdvanceMs?: number;
  /** Shown only when a previous auth step exists, such as password reset. */
  backLabel?: string;
  onBack?: () => void;
  /** Scale form content to fit viewport height (login). Signup should disable and scroll. */
  fitContentScale?: boolean;
}

function useAuthPanelContentScale(enabled: boolean) {
  const panelRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  const fitContent = useCallback(() => {
    if (!enabled) {
      setScale(1);
      return;
    }
    const panel = panelRef.current;
    const content = contentRef.current;
    if (!panel || !content) return;
    const style = getComputedStyle(panel);
    const available =
      panel.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
    const height = content.offsetHeight;
    setScale(height && available > 0 ? Math.min(1, available / height) : 1);
  }, [enabled]);

  useEffect(() => {
    fitContent();
    window.addEventListener('resize', fitContent);
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(fitContent);
      if (panelRef.current) observer.observe(panelRef.current);
      if (contentRef.current) observer.observe(contentRef.current);
    }
    return () => {
      window.removeEventListener('resize', fitContent);
      observer?.disconnect();
    };
  }, [fitContent]);

  return { panelRef, contentRef, scale };
}

export function AuthPageShell({
  title,
  subtitle,
  badge,
  children,
  footer,
  activeAudience,
  plantationCrop,
  onAudienceChange,
  onPlantationCropChange,
  autoAdvanceMs,
  backLabel = 'Back',
  onBack,
  fitContentScale = true,
}: AuthPageShellProps) {
  const [panelAudience, setPanelAudience] = useState<AuthAudienceId>(activeAudience ?? 'blo');
  const { panelRef, contentRef, scale } = useAuthPanelContentScale(fitContentScale);

  useEffect(() => {
    if (activeAudience) setPanelAudience(activeAudience);
  }, [activeAudience]);

  const handleAudienceChange = (id: AuthAudienceId) => {
    setPanelAudience(id);
    onAudienceChange?.(id);
  };

  const panelImage = AUTH_PANEL_IMAGES[panelAudience] ?? authBlo;

  return (
    <div
      className="flex h-[100dvh] w-full overflow-hidden lg:items-stretch"
      style={{ background: AUTH_PAPER, colorScheme: 'light' }}
    >
      <div
        className="relative hidden min-h-0 overflow-hidden lg:flex lg:min-h-0 lg:flex-1 lg:flex-col lg:px-11 lg:pb-7 lg:pt-10"
        style={{ color: AUTH_PAPER, fontFamily: AUTH_FONT_SANS }}
      >
        <img
          key={panelAudience}
          src={panelImage}
          alt=""
          className="absolute inset-0 h-full w-full scale-[1.03] object-cover object-left"
        />
        {/* Soft focus feel: keep photo visible; darken mainly under UI (left), open sky/fields on the right */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: [
              'linear-gradient(90deg, rgba(8,28,18,0.82) 0%, rgba(8,28,18,0.55) 38%, rgba(8,28,18,0.22) 68%, rgba(8,28,18,0.30) 100%)',
              'linear-gradient(180deg, rgba(8,28,18,0.42) 0%, transparent 22%, transparent 70%, rgba(8,28,18,0.58) 100%)',
            ].join(', '),
          }}
          aria-hidden
        />
        {/* Gentle vignette so the person / mid subject reads sharper than the edges */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(80% 70% at 28% 45%, transparent 0%, transparent 42%, rgba(8,28,18,0.28) 100%)',
          }}
          aria-hidden
        />
        <div className="pointer-events-none absolute inset-0 opacity-[0.10]" aria-hidden>
          <FieldBoundaryLines />
        </div>

        <AuthFeatureShowcase
          activeAudience={panelAudience}
          plantationCrop={plantationCrop}
          onAudienceChange={handleAudienceChange}
          onPlantationCropChange={onPlantationCropChange}
          autoAdvanceMs={autoAdvanceMs}
        />
      </div>

      <div
        ref={panelRef}
        className={`login-panel flex h-full min-h-0 w-full justify-center px-5 py-10 sm:px-10 lg:w-[min(100%,36rem)] lg:shrink-0 lg:px-12 lg:py-12 xl:w-[41rem] ${
          fitContentScale ? 'items-center overflow-hidden' : 'items-start overflow-y-auto'
        }`}
        style={{ background: AUTH_PAPER, fontFamily: AUTH_FONT_SANS }}
      >
        <div
          ref={contentRef}
          className="login-content w-full max-w-lg shrink-0"
          style={{
            transform: fitContentScale ? `scale(${scale})` : undefined,
            transformOrigin: 'center',
          }}
        >
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="group mb-3 inline-flex w-fit items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium shadow-sm transition hover:border-[#00854080] hover:text-[#008540]"
              style={{
                borderColor: AUTH_LINE,
                background: AUTH_SURFACE,
                color: AUTH_MUTED,
              }}
            >
              <i className="ri-arrow-left-line text-sm transition group-hover:-translate-x-0.5" aria-hidden />
              {backLabel}
            </button>
          )}

          <div className="login-brand mb-8 flex justify-center">
            <img
              src={logo}
              alt="Digisaka"
              className="h-10 w-auto object-contain sm:h-11"
            />
          </div>

          <div className="login-heading mb-7 text-center">
            {badge && (
              <p
                className="mb-2 text-xs font-semibold uppercase tracking-[0.15em]"
                style={{ color: AUTH_GREEN }}
              >
                {badge}
              </p>
            )}
            <h1 className="text-3xl font-semibold tracking-tight" style={{ color: AUTH_INK }}>
              {title}
            </h1>
            <p className="mt-2 text-sm leading-6" style={{ color: AUTH_MUTED }}>
              {subtitle}
            </p>
            <Link
              to="/sflma"
              className="mt-4 inline-flex rounded-full border px-3 py-1.5 text-xs font-semibold lg:hidden"
              style={{ borderColor: AUTH_GREEN_BORDER, color: AUTH_GREEN }}
            >
              Open the carbon explorer
            </Link>
          </div>

          <div>{children}</div>

          {footer && <footer className="login-footer mt-7">{footer}</footer>}
        </div>
      </div>
    </div>
  );
}

interface AuthInputProps {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  autoComplete?: string;
  icon?: string;
  hint?: string;
  labelExtra?: ReactNode;
  disabled?: boolean;
  /** Password fields can reveal the typed value. */
  passwordToggle?: boolean;
}

export function AuthInput({
  id,
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  required,
  minLength,
  maxLength,
  autoComplete,
  icon,
  hint,
  labelExtra,
  disabled,
  passwordToggle = false,
}: AuthInputProps) {
  const [revealed, setRevealed] = useState(false);
  const inputType = passwordToggle && type === 'password' && revealed ? 'text' : type;

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <label htmlFor={id} className="text-sm font-medium" style={{ color: AUTH_LABEL }}>
          {label}
        </label>
        {labelExtra}
      </div>
      <div className="relative">
        {icon && (
          <i
            className={`${icon} pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xl leading-none`}
            style={{ color: AUTH_PLACEHOLDER }}
            aria-hidden
          />
        )}
        <input
          id={id}
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          minLength={minLength}
          maxLength={maxLength}
          autoComplete={autoComplete}
          placeholder={placeholder}
          disabled={disabled}
          className={`auth-field w-full rounded-xl border py-3 text-sm text-gray-900 shadow-sm outline-none transition placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-60 ${
            icon ? 'pl-10' : 'pl-3.5'
          } ${passwordToggle ? 'pr-10' : 'pr-3.5'}`}
          style={{
            borderColor: AUTH_LINE,
            background: AUTH_SURFACE,
            color: AUTH_INK,
            caretColor: AUTH_INK,
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = AUTH_GREEN;
            e.currentTarget.style.boxShadow = `0 0 0 2px ${AUTH_GREEN_SOFT}`;
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = AUTH_LINE;
            e.currentTarget.style.boxShadow = 'none';
          }}
        />
        {passwordToggle && type === 'password' && (
          <button
            type="button"
            onClick={() => setRevealed((open) => !open)}
            className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-xl transition hover:text-gray-600"
            style={{ color: AUTH_PLACEHOLDER }}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
          >
            <i className={revealed ? 'ri-eye-off-line text-xl' : 'ri-eye-line text-xl'} aria-hidden />
          </button>
        )}
      </div>
      {hint && (
        <p className="mt-1 text-[11px] leading-snug" style={{ color: AUTH_MUTED }}>
          {hint}
        </p>
      )}
    </div>
  );
}

interface AuthSelectProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  hint?: string;
}

export function AuthSelect({ id, label, value, onChange, options, hint }: AuthSelectProps) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium" style={{ color: AUTH_LABEL }}>
        {label}
      </label>
      <div className="relative">
        <i
          className="ri-user-settings-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xl leading-none"
          style={{ color: AUTH_PLACEHOLDER }}
          aria-hidden
        />
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="auth-field w-full appearance-none rounded-xl border py-3 pl-10 pr-9 text-sm text-gray-900 shadow-sm outline-none transition"
          style={{
            borderColor: AUTH_LINE,
            background: AUTH_SURFACE,
            color: AUTH_INK,
            caretColor: AUTH_INK,
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = AUTH_GREEN;
            e.currentTarget.style.boxShadow = `0 0 0 2px ${AUTH_GREEN_SOFT}`;
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = AUTH_LINE;
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <i
          className="ri-arrow-down-s-line pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-xl leading-none"
          style={{ color: AUTH_PLACEHOLDER }}
          aria-hidden
        />
      </div>
      {hint && (
        <p className="mt-1 text-[11px] leading-snug" style={{ color: AUTH_MUTED }}>
          {hint}
        </p>
      )}
    </div>
  );
}

export function AuthError({ message }: { message: string }) {
  return (
    <div
      className="mb-5 flex gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm leading-6 text-red-800"
      role="alert"
    >
      <i className="ri-error-warning-line mt-0.5 shrink-0 text-lg text-red-500" aria-hidden />
      <span>{message}</span>
    </div>
  );
}

export function AuthSuccess({ message }: { message: string }) {
  return (
    <div
      className="mb-3 flex gap-2 rounded-lg border px-3 py-2 text-xs"
      style={{ borderColor: AUTH_GREEN_BORDER, backgroundColor: AUTH_GREEN_LIGHT, color: AUTH_GREEN }}
      role="status"
    >
      <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-lg" style={{ color: AUTH_GREEN }} aria-hidden />
      <span>{message}</span>
    </div>
  );
}

interface AuthButtonProps {
  children: ReactNode;
  type?: 'button' | 'submit';
  variant?: 'primary' | 'secondary' | 'ghost';
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  className?: string;
}

export function AuthButton({
  children,
  type = 'button',
  variant = 'primary',
  disabled,
  loading,
  onClick,
  className = '',
}: AuthButtonProps) {
  const base =
    'inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70';
  const variantClass =
    variant === 'primary'
      ? 'text-white shadow-sm hover:brightness-[0.92]'
      : variant === 'secondary'
        ? 'border-2 hover:bg-[#00854014]'
        : 'hover:bg-[#00854014]';

  const variantStyle =
    variant === 'primary'
      ? { background: AUTH_GREEN, color: '#fff' }
      : variant === 'secondary'
        ? {
            background: AUTH_SURFACE,
            borderColor: AUTH_GREEN_BORDER,
            color: AUTH_GREEN,
          }
        : { color: AUTH_GREEN, background: 'transparent' };

  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className={`${base} ${variantClass} focus-visible:ring-[#008540]/30 ${className}`}
      style={variantStyle}
    >
      {loading && (
        <span
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden
        />
      )}
      {children}
    </button>
  );
}

export function AuthDivider({ label }: { label: string }) {
  return (
    <div className="relative my-3">
      <div className="absolute inset-0 flex items-center" aria-hidden>
        <div className="w-full border-t" style={{ borderColor: AUTH_LINE }} />
      </div>
      <div className="relative flex justify-center">
        <span
          className="px-3 text-xs font-medium uppercase tracking-wide"
          style={{ background: AUTH_PAPER, color: AUTH_PLACEHOLDER }}
        >
          {label}
        </span>
      </div>
    </div>
  );
}

interface AuthPolicyFooterProps {
  onOpenPrivacy: () => void;
  onOpenTerms: () => void;
  switchPrompt: string;
  switchLabel: string;
  onSwitch: () => void;
}

export function AuthPolicyFooter({
  onOpenPrivacy,
  onOpenTerms,
  switchPrompt,
  switchLabel,
  onSwitch,
}: AuthPolicyFooterProps) {
  return (
    <div className="space-y-2 text-center text-xs leading-6" style={{ color: AUTH_MUTED }}>
      <p className="text-sm" style={{ color: AUTH_MUTED }}>
        {switchPrompt}{' '}
        <button
          type="button"
          onClick={onSwitch}
          className="font-semibold transition hover:underline"
          style={{ color: AUTH_GREEN }}
        >
          {switchLabel}
        </button>
      </p>
      <nav className="flex justify-center gap-5" aria-label="Legal">
        <button
          type="button"
          onClick={onOpenPrivacy}
          className="rounded underline-offset-4 transition hover:text-[#008540] hover:underline"
          style={{ color: AUTH_MUTED }}
        >
          Privacy Policy
        </button>
        <button
          type="button"
          onClick={onOpenTerms}
          className="rounded underline-offset-4 transition hover:text-[#008540] hover:underline"
          style={{ color: AUTH_MUTED }}
        >
          Terms &amp; Conditions
        </button>
      </nav>
    </div>
  );
}
